import { MailOutlined } from '@ant-design/icons';
import {
  Button,
  ConfigProvider,
  Flex,
  Form,
  Input,
  type InputRef,
  Statistic,
  theme,
  Typography,
} from 'antd';
import { type ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { catchError, EMPTY, finalize } from 'rxjs';
import { AuthContext } from '../../contexts/auth-context-provider';

const requiredTokenMessage = "Please enter the one-time token you've received!";
const numberTokenMessage = 'Passcode must be all numbers!';

const LoginVerifyForm = (props: { email: string; token?: string }): ReactNode => {
  const tokenConfig = {
    length: Number.parseInt(process.env.REACT_APP_OTP_LENGTH ?? '6'),
    lifetime: Number.parseInt(process.env.REACT_APP_OTP_LIFETIME ?? '120000'),
  };
  const { email, token: ott } = props;
  const { login, loginVerify } = useContext(AuthContext);
  const { token } = theme.useToken();
  const [formInstance] = Form.useForm();
  const [isSubmitting, setSubmitting] = useState(false);
  const [isTokenExpired, setTokenExpired] = useState(false);
  const tokenExpirationTime = useRef(Date.now() + tokenConfig.lifetime);
  const inputTokenRef = useRef<InputRef>(null);
  const initialValues = useMemo(() => ({ email, token: ott ?? '' }), [email, ott]);

  useEffect(() => {
    if (email && ott) {
      // If both email and token are provided, submit the verification immediately
      const x = setTimeout(() => {
        isFormValid() && submitLoginVerify({ email, token: ott });
      }, 10);

      return () => {
        clearTimeout(x);
      };
    }
  }, []);

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [initialValues]);

  const isFormValid = (): boolean =>
    formInstance.getFieldsError().every((item) => item.errors.length === 0);

  const resendCode = (): void => {
    setSubmitting(true);
    login(email)
      .pipe(
        finalize(() => {
          formInstance.setFields([
            {
              name: 'token',
              value: '',
              errors: [],
            },
          ]);
          inputTokenRef.current!.focus({
            cursor: 'start',
          });
          tokenExpirationTime.current = Date.now() + tokenConfig.lifetime;
          setSubmitting(false);
          setTokenExpired(false);
        }),
        catchError((_error) => {
          return EMPTY;
        })
      )
      .subscribe();
  };

  const submitLoginVerify = (values: { email: string; token: string }): void => {
    const { email, token } = values;
    setSubmitting(true);
    loginVerify(email, token)
      .pipe(
        finalize(() => {
          setSubmitting(false);
        }),
        catchError((error) => {
          formInstance.setFields([
            {
              name: 'token',
              errors: [error.message],
            },
          ]);
          return EMPTY;
        })
      )
      .subscribe();
  };

  return (
    <Form
      layout="vertical"
      form={formInstance}
      initialValues={initialValues}
      style={{ width: '270px' }}
      onValuesChange={(_, values) =>
        setTimeout(() => {
          isFormValid() && submitLoginVerify(values);
        }, 10)
      } // no isFormValid check out-of-box
      requiredMark={false}
      colon={false}
    >
      <Form.Item name="email">
        <Input
          prefix={<MailOutlined className="site-form-item-icon" />}
          placeholder="Email"
          size="large"
          variant="borderless"
          styles={{
            prefix: { marginInlineEnd: token.marginSM, marginInlineStart: 0 },
            affixWrapper: { padding: 0 },
          }}
          ref={inputTokenRef}
          disabled
        />
      </Form.Item>
      <Form.Item
        label="Check your email for passcode"
        name="token"
        rules={[
          { required: true, message: requiredTokenMessage },
          { pattern: /^\d+$/, message: numberTokenMessage },
        ]}
        hasFeedback
        validateFirst={true}
        validateTrigger="onChange"
      >
        <Input.OTP
          length={tokenConfig.length}
          size="large"
          disabled={isSubmitting || isTokenExpired}
          style={{ marginBlockStart: token.marginSM, width: '100%' }}
          ref={(ref) =>
            ref?.nativeElement.firstElementChild?.setAttribute('autocomplete', 'one-time-code')
          }
        />
      </Form.Item>
      <Form.Item style={{ marginBlockEnd: 0 }}>
        <ConfigProvider theme={{ components: { Statistic: { contentFontSize: 14 } } }}>
          {!isTokenExpired && (
            <Flex gap={5} justify="center" align="center">
              <Typography.Text key="expiry-time">Will expire in </Typography.Text>
              <Statistic.Countdown
                key="countdown"
                value={tokenExpirationTime.current}
                format="mm:ss"
                onFinish={() => {
                  setTokenExpired(true);
                }}
              ></Statistic.Countdown>
            </Flex>
          )}
          {isTokenExpired && (
            <Flex vertical gap={1} justify="center" align="center">
              <Typography.Text key="expiry-time">Passcode has expired!</Typography.Text>
              <Button type="link" onClick={resendCode}>
                Resend a new one
              </Button>
            </Flex>
          )}
        </ConfigProvider>
      </Form.Item>
    </Form>
  );
};

export default LoginVerifyForm;
